import { CollisionShape, SceneId } from '../api';
import { LOS_CONFIG } from '../config';
import { EquipSlot } from '../data/BodyTypeSlots';
import { SpotActionId } from '../data/SpotActions';
import { DEFAULT_INPUT_STATE, InputState } from '../input';

// Re-export EquipSlot for consumers that import from components
export type { EquipSlot };

export type InteractionType = 'talk' | 'loot' | 'pickpocket' | 'portal' | 'bed';

export interface MarkedForDestructionData {}

/**
 * Stores the movement speeds for walk and run modes in pixels per second.
 * State-driven movement systems use these values based on the current MovementMode.
 */
export interface MovementSpeedsData {
  walk: number;
  run: number;
}

/**
 * Tracks the current movement mode of an entity.
 * Influences animation selection and effective movement speed.
 */
export interface MovementModeData {
  mode: 'walk' | 'run';
}

export interface SpeedMultiplierData {
  multiplier: number;
}

export interface IsMovingData {}
export interface FastMovementData {}

/**
 * Marker for an entity that is a client-side prediction.
 * Used for reconciliation and to skip processing of replicated entities in predicted systems.
 */
export interface IsPredictedData {}

/**
 * Marker for an entity that is an item (e.g. wood, sword).
 * Contains the ItemRegistry ID.
 */
export interface ItemData {
  itemId: string;
}

/**
 * Marker for an item that is lying on the ground.
 * These entities are valid targets for magnetic pickup.
 */
export interface GroundItemData {}

/**
 * Defines the magnetic pickup radius for an entity (usually a player).
 * Entities with this component will attract GroundItems within the radius.
 */
export interface MagneticPickupRadiusData {
  /** Radius in pixels */
  radius: number;
}

/**
 * Component added to a GroundItem when it is being magnetically attracted.
 * Uniquely identifies which entity is picking it up to prevent conflicts.
 */
export interface BeingPickedUpData {
  targetEntityId: number;
}

/**
 * Prevents an item from being picked up for a short duration.
 * Used when pickup fails (e.g. inventory full) to prevent infinite loops.
 */
export interface PickupCooldownData {
  cooldownRemaining: number;
}

/**
 * Tracks who dropped an item. Used to prevent immediate re-pickup.
 * Removed when dropper leaves pickup radius of this item.
 */
export interface DroppedByData {
  /** Entity ID that dropped this item */
  dropperEntityId: number;
}

/**
 * Marks an item as currently being thrown (drop animation in progress).
 * Contains trajectory info for ItemThrowSystem.
 */
export interface BeingThrownData {
  /** Target landing position */
  targetX: number;
  targetY: number;
  /** Final rotation when landing */
  targetRotation: number;
  /** Entity that threw/dropped the item */
  dropperEntityId: number;
}

export type { SceneId };

export interface TransformData {
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  sceneId: SceneId;
}

export interface VelocityData {
  dx: number;
  dy: number;
}

export interface SpriteData {
  textureId: string;
  anchorX: number;
  anchorY: number;
  tint: number;
  visible: boolean;
  zIndexOffset: number;
}

export interface PlayerControlledData {
  playerId: number;
}

export interface ColliderData {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
  isSensor: boolean;
}

/**
 * Hitbox for combat (hit detection).
 * Uses top-left origin convention: offsetX/Y are from transform position to box top-left.
 */
export interface HitboxData {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

/**
 * The permanent system ID for a spawned NPC (e.g. 'digory_vingoe').
 * Used to re-register the NPC with the registry when restored from persistence.
 */
export interface NpcSystemIdData {
  npcId: string;
}

/**
 * Target location for an entity to move toward.
 */
export interface DestinationData {
  x: number;
  y: number;
  sceneId: SceneId;
  range: number; // Distance in pixels to consider "reached" (e.g., 16 for same tile)
}

/**
 * Ultimate destination intent for an entity.
 * Decoupled from immediate pathfinding steps.
 */
export interface NavIntentData {
  x: number;
  y: number;
  sceneId: SceneId;
  /** Distance (pixels) to consider goal reached */
  range: number;
}

/**
 * Target for an entity to follow.
 */
export interface FollowTargetData {
  targetEntityId: number;
  /** Ideal distance to maintain from target */
  offset: number;
}

// InputData is derived from InputState for type safety (single source of truth in input.ts)
export type InputData = InputState;

/**
 * Debug color for visualization (unique per entity).
 */
export interface DebugColorData {
  color: number; // hex color e.g. 0xff0000
}

/**
 * A waypoint in a computed path.
 */
export interface PathWaypointData {
  x: number;
  y: number;
  sceneId: SceneId;
}

/**
 * Computed path of waypoints for navigation.
 */
export interface PathData {
  waypoints: PathWaypointData[];
  currentWaypointIndex: number;
  // Track what destination this path was computed for (to avoid recomputing)
  computedForDestination: { x: number; y: number; sceneId: SceneId };
}

/** Destination: linked to another portal by name */
export interface LinkedDestination {
  destinationType: 'linked';
  linkedTo: string;
}

/** Destination: explicit scene coordinates */
export interface CoordinatesDestination {
  destinationType: 'coordinates';
  scene: string;
  x: number;
  y: number;
}

/** Union of portal destination types */
export type PortalDestination = LinkedDestination | CoordinatesDestination;

/**
 * Portal component for scene teleportation.
 * - name: Unique identifier from Tiled object name attribute
 * - triggerType: 'door' requires interaction, 'entrance' triggers on walk-in
 * - destination: Where the portal leads (linked to another portal OR explicit coordinates)
 */
export interface PortalData {
  name: string;
  triggerType: 'door' | 'entrance';
  destination: PortalDestination;
}

/**
 * Trigger zone for detecting entity overlap.
 * Used for entrance portals that activate on walk-in, future triggers (traps, buttons, etc.).
 */
export interface TriggerZoneData {
  /** Trigger shapes in local coordinates (relative to entity transform) */
  shapes: CollisionShape[];
}

/**
 * Reference to a spritesheet and current frame index.
 * Used by SpritesheetResolverSystem to set Sprite.textureId.
 */
export interface SpritesheetSpriteData {
  /** Spritesheet identifier (matches filename without extension) */
  spritesheetId: string;
  /** 1-based frame index in the spritesheet */
  frameIndex: number;
}

/** Marker: entity supports walking animations */
export interface HasWalkingAnimationData {}

/** Marker: entity supports idle animation */
export interface HasIdleAnimationData {}

/** Cardinal direction an entity is facing */
export type FacingDirection = 'up' | 'down' | 'left' | 'right';

/**
 * The direction an entity is currently facing.
 * Updated by FacingDirectionSystem based on movement intent.
 * Used by animation systems to select directional animations.
 */
export interface FacingData {
  direction: FacingDirection;
}

/**
 * Defines the tile range used to approximate an entity's line of sight.
 */
export interface LineOfSightData {
  tileRange: number;
}

/** Marker: entity sprite is mirrored via scaleX when facing left */
export interface MirrorXData {}

/**
 * Marks an entity as a bed.
 * This is the runtime interactable object used for entering/leaving bed,
 * separate from authored sleep spot metadata.
 */
export interface BedData {
  ownedBy: string;
}

/**
 * Canonical schedule role labels used by authored behaviour spots.
 */
export type BehaviourRole = string;

/**
 * One authored behaviour role attached to a map object.
 * Multiple entries can live on the same object so a bed, bar, or other object can
 * serve different daily schedule roles without duplicate map geometry.
 */
export interface BehaviourSpotEntryData {
  /** Canonical schedule role label for this entry (e.g., 'Idle', 'Sleep', or 'Crops'). */
  role: BehaviourRole;
  /** Factions that consider this a valid behaviour spot (e.g., ['faction/Sennen']) */
  factions: string[];
  /** NPC system id that owns this behaviour spot, if any */
  ownedBy: string;
  /** Actions available at this spot entry (e.g., ['wander', 'sleep']) */
  spotActions: SpotActionId[];
}

/**
 * Collection of behaviour entries attached to a single map object.
 */
export interface BehaviourSpotData {
  entries: BehaviourSpotEntryData[];
}

/**
 * Legacy aliases kept temporarily for compatibility with any remaining spot-specific imports.
 * These collapse to the shared entry shape so callers can migrate to BehaviourSpotData.
 */
export type IdleSpotData = BehaviourSpotEntryData;
export type SleepSpotData = BehaviourSpotEntryData;
export type WorkSpotData = BehaviourSpotEntryData;

/**
 * Marks an entity as currently in a bed.
 * Used to suppress movement and track entry/exit logic.
 */
export interface InBedData {
  bedEntityId: number | null;
  entryX: number;
  entryY: number;
}

/**
 * Marks an entity as interactable by the player.
 * Static entities use spatial index for efficient lookup.
 */
export interface InteractableData {
  isStatic: boolean;
}

/**
 * Marks an entity as capable of dialogue.
 */
export interface CanTalkData {
  inkScriptId: string;
}

export interface InteractionHintData {
  text: string;
  type?: string; // Optional interaction type ID
}

/**
 * Tracks an active interaction between two entities.
 * Usually added to the source (player/NPC) of the interaction.
 */
export interface InteractingWithData {
  targetEntityId: number;
  type: InteractionType;
}

/**
 * Light source for darkness/lighting system.
 * Entities with this component emit light in a radius.
 */
export interface LightSourceData {
  /** Light intensity (0 = no light, 15 = maximum brightness) */
  strength: number;
  /** Light radius in pixels */
  distance: number;
  /** Light color as hex (e.g., 0xFFAA00 for warm light) */
  color: number;
  /** Light color alpha/intensity (0-1) */
  colorAlpha: number;
  /** If true, only visible to the owning player (for visibility halos) */
  localOnly: boolean;
}

/**
 * Computed world-space transform.
 * Populated by TransformPropagationSystem from hierarchy.
 * For root entities, this equals Transform values.
 * For children, this is parent's GlobalTransform composed with local Transform.
 */
export interface GlobalTransformData {
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  sceneId?: SceneId;
}

/**
 * Marks an entity as a child of another entity.
 * This is the source of truth for parent-child relationships.
 * When ChildOf is added/removed, the parent's Children component
 * is automatically updated by World.addChildren() / removeChildren().
 */
export interface ChildOfData {
  /** Entity ID of the direct parent */
  parent: number;
  /** Entity ID of the root ancestor (top of the hierarchy) */
  root: number;
}

/**
 * Lists all direct children of this entity.
 * This component is automatically managed - do not modify directly.
 * Use World.addChildren() and World.removeChildren() instead.
 */
export interface ChildrenData {
  children: number[]; // Array of child entity IDs
}

/** Marker: entity can trigger TriggerZones (entrance portals, floor traps, etc.) */
export interface CanTriggerData {}

/** Marker: entity can interact with Interactable entities (doors, buttons, etc.) */
export interface CanInteractData {}

/**
 * Marks an entity as currently in a dialogue conversation.
 * Synced to all clients in AOI so they can see entities are busy.
 */
export interface InConversationData {
  /** The other participant in the conversation */
  withEntityId: number;
}

/**
 * Display name for entities (items, NPCs, etc.).
 */
export interface DisplayNameData {
  name: string;
}

/**
 * Inventory containing item entities.
 * Each slot holds an entity ID referencing an item, or null if empty.
 */
export interface InventoryData {
  slots: (number | null)[];
  maxSlots: number;
}

/**
 * Stack information for stackable items.
 * Items without this component are implicitly quantity 1.
 */
export interface StackData {
  quantity: number;
  maxStack: number;
}

/**
 * Defines the "body type" of an entity, which determines what equipment slots are available.
 * Slot types are resolved from BodyTypeSlots registry in common/data.
 */
export interface BodyTypeData {
  bodyType: string; // e.g. 'humanoid', 'mount', 'beast'
}

/**
 * Equipped items on an entity.
 * Keys are slot names, values are item entity IDs.
 * Valid slots are determined by the entity's BodyType via BodyTypeSlots registry.
 */
export interface EquipmentData {
  slots: Partial<Record<EquipSlot, number | null>>;
}

/**
 * Marks an item as equippable in specific non-hand slots.
 * Hand slots (mainHand, offHand) accept any item regardless of this component.
 * Other slots (head, body, etc.) require this component with matching validSlots.
 */
export interface EquippableData {
  validSlots: EquipSlot[];
}

/**
 * Combat stats for weapons.
 * Attached to item entities.
 */
export interface WeaponData {
  damage: number;
  range: number;
  arc: number; // In degrees, e.g., 90 for a sweep
  cooldownMs: number;
  knockbackForce: number;
  swingPattern: 'stab' | 'arc';
  hitboxCount: number;
  bladeWidth: number;
}

/**
 * Tracking for attack cooldowns.
 * Attached to entities that can attack (players, NPCs).
 */
export interface AttackCooldownData {
  remainingMs: number;
}

/**
 * Transient force applied to an entity (impulse).
 * Decayed over time by KnockbackSystem.
 */
export interface KnockbackData {
  dx: number;
  dy: number;
}

/**
 * Health information for an entity.
 * current: Current health points.
 * max: Maximum health points reachable via healing.
 */
export interface HealthData {
  /** Current health points. Can be negative (dead). */
  current: number;
  /** Maximum health capacity */
  max: number;
}

/**
 * Marker component for dead entities.
 */
export interface DeadData {}

/**
 * Skin color for entities (HEX value).
 */
export interface SkinColourData {
  colour: number;
}

/**
 * OCEAN character traits for NPCs.
 * Values should be between 1-10.
 */
export interface OceanData {
  openness: number;
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
}

/**
 * Data for face decorations (eyes, mouth) and their custom colours.
 */
export interface FaceDecorationData {
  /** Spritesheet ID for eyes (e.g., 'eyes-1', 'eyes-2') */
  eyesStyle: string;
  /** Spritesheet ID for mouth (e.g., 'mouth-1', 'mouth-2') */
  mouthStyle: string;
  /** Primary colour for eyes (iris) - hex */
  eyePrimaryColour: number;
  /** Secondary colour for eyes (optional, e.g., eyeshadow) - hex */
  eyeSecondaryColour: number;
  /** Primary colour for mouth (lips) - hex */
  mouthPrimaryColour: number;
  /** Secondary colour for mouth (optional, e.g., lipstick highlight) - hex */
  mouthSecondaryColour: number;
}

/**
 * Current aggro target for an entity.
 * Used by NPC AI to decide who to attack or follow.
 */
export interface AggroData {
  targetEntityId: number;
}

/**
 * Data for a weapon swing entity.
 */
export interface WeaponSwingData {
  ownerEntityId: number; // Who initiated the attack
  hitEntities: number[]; // Entities already hit this swing
  progress: number; // 0.0 to 1.0 animation progress
  elapsedMs: number; // Time since swing started
  durationMs: number; // Total swing duration
  maxExtension: number; // How far weapon extends (weapon range)
  hitboxIndex: number; // Which hitbox this entity represents (0 to hitboxCount-1)
}

/**
 * Lists all factions an entity belongs to.
 */
export interface FactionsData {
  factionIds: string[];
}

/**
 * Specific relationship overrides toward other entities.
 * Key: target entity ID, Value: disposition score (0-100).
 */
export interface RelationshipsData {
  overrides: Record<number, number>;
}

/**
 * Current calculated disposition toward another entity.
 * This is usually transient and recalculated by systems.
 */
export interface DispositionData {
  score: number;
  towardEntityId: number;
}

export const CommonDefaults = {
  Velocity: {
    dx: 0,
    dy: 0,
  },
  Collider: {
    width: 16,
    height: 16,
    offsetX: 0,
    offsetY: 0,
    isSensor: false,
  },
  Hitbox: {
    width: 16,
    height: 32,
    offsetX: -8,
    offsetY: -32,
  },
  Sprite: {
    anchorX: 0.5,
    anchorY: 1,
    tint: 0xffffff,
    visible: true,
    zIndexOffset: 0,
  },
  Input: DEFAULT_INPUT_STATE,
  MovementSpeeds: {
    walk: 50,
    run: 100,
  },
  MovementMode: {
    mode: 'walk' as const,
  },
  LightSource: {
    strength: 8,
    distance: 64,
    color: 0xffffff,
    colorAlpha: 1,
    localOnly: false,
  },
  Bed: {
    ownedBy: '',
  },
  BehaviourSpot: {
    entries: [],
  },
  InBed: {
    bedEntityId: null as number | null,
    entryX: 0,
    entryY: 0,
  },
  Transform: {
    x: 0,
    y: 0,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
  },
  GlobalTransform: {
    x: 0,
    y: 0,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
  },
  Portal: {
    name: '',
    triggerType: 'door' as const,
    destination: {
      destinationType: 'coordinates' as const,
      scene: '',
      x: 0,
      y: 0,
    },
  },
  SpritesheetSprite: {
    frameIndex: 1,
  },
  Facing: {
    direction: 'down' as FacingDirection,
  },
  LineOfSight: {
    tileRange: LOS_CONFIG.DEFAULT_TILE_RANGE,
  },
  Inventory: {
    slots: [],
    maxSlots: 10,
  },
  Stack: {
    quantity: 1,
    maxStack: 1,
  },
  MagneticPickupRadius: {
    radius: 32, // Default to 2 tiles
  },
  BodyType: {
    bodyType: 'humanoid',
  },
  Equipment: {
    slots: {},
  },
  AttackCooldown: { remainingMs: 0 },
  Knockback: { dx: 0, dy: 0 },
  Weapon: {
    damage: 0,
    range: 0,
    arc: 0,
    cooldownMs: 0,
    knockbackForce: 0,
    swingPattern: 'arc',
    hitboxCount: 3,
    bladeWidth: 8,
  },
  Health: {
    current: 100,
    max: 100,
  },
  CanTalk: { inkScriptId: '' },
  InteractionHint: { text: '' },
  Aggro: { targetEntityId: -1 },
  WeaponSwing: {
    ownerEntityId: -1,
    hitEntities: [],
    progress: 0,
    elapsedMs: 0,
    durationMs: 0,
    maxExtension: 0,
    hitboxIndex: 0,
  },
  InteractingWith: { targetEntityId: -1, type: 'talk' as InteractionType },
  SpeedMultiplier: { multiplier: 1 },
  IsPredicted: {},
  SkinColour: {
    colour: 0xffffff, // Default to pure white (no tint)
  },
  FaceDecoration: {
    eyesStyle: 'eyes-1',
    mouthStyle: 'mouth-1',
    eyePrimaryColour: 0xffffff,
    eyeSecondaryColour: 0xffffff,
    mouthPrimaryColour: 0xffffff,
    mouthSecondaryColour: 0xffffff,
  },
  Factions: {
    factionIds: [],
  },
  Relationships: {
    overrides: {},
  },
  Disposition: {
    score: 50,
    towardEntityId: -1,
  },
  Ocean: {
    openness: 5,
    conscientiousness: 5,
    extraversion: 5,
    agreeableness: 5,
    neuroticism: 5,
  },
  FastMovement: {},
};

export interface CommonRegistry {
  IsMoving: IsMovingData;
  Transform: TransformData;
  Velocity: VelocityData;
  Sprite: SpriteData;
  MovementSpeeds: MovementSpeedsData;
  MovementMode: MovementModeData;
  PlayerControlled: PlayerControlledData;
  MarkedForDestruction: MarkedForDestructionData;
  Collider: ColliderData;
  Hitbox: HitboxData;

  Input: InputData;
  Destination: DestinationData;
  DebugColor: DebugColorData;
  Path: PathData;
  Portal: PortalData;
  Interactable: InteractableData;
  Bed: BedData;
  BehaviourSpot: BehaviourSpotData;
  InBed: InBedData;
  InteractingWith: InteractingWithData;
  LightSource: LightSourceData;
  GlobalTransform: GlobalTransformData;
  ChildOf: ChildOfData;
  Children: ChildrenData;
  TriggerZone: TriggerZoneData;
  SpritesheetSprite: SpritesheetSpriteData;
  HasWalkingAnimation: HasWalkingAnimationData;
  HasIdleAnimation: HasIdleAnimationData;
  Facing: FacingData;
  LineOfSight: LineOfSightData;
  MirrorX: MirrorXData;
  CanTrigger: CanTriggerData;
  CanInteract: CanInteractData;
  InConversation: InConversationData;
  Item: ItemData;
  GroundItem: GroundItemData;
  MagneticPickupRadius: MagneticPickupRadiusData;
  BeingPickedUp: BeingPickedUpData;
  PickupCooldown: PickupCooldownData;
  DroppedBy: DroppedByData;
  BeingThrown: BeingThrownData;
  DisplayName: DisplayNameData;
  Inventory: InventoryData;
  Stack: StackData;
  BodyType: BodyTypeData;
  Equipment: EquipmentData;
  Equippable: EquippableData;
  Weapon: WeaponData;
  AttackCooldown: AttackCooldownData;
  Knockback: KnockbackData;

  Health: HealthData;
  Dead: DeadData;
  CanTalk: CanTalkData;
  InteractionHint: InteractionHintData;
  Aggro: AggroData;
  WeaponSwing: WeaponSwingData;
  SpeedMultiplier: SpeedMultiplierData;
  NavIntent: NavIntentData;
  FollowTarget: FollowTargetData;
  IsPredicted: IsPredictedData;
  SkinColour: SkinColourData;
  FaceDecoration: FaceDecorationData;
  Factions: FactionsData;
  Relationships: RelationshipsData;
  Disposition: DispositionData;
  Ocean: OceanData;
  FastMovement: FastMovementData;
  readonly __defaults: typeof CommonDefaults;
}
